import { useQuery } from '@tanstack/react-query'
import { controlCenterApi, type DnsRecord } from '@/lib/api'
import PageHeader from '@/components/PageHeader'

type DnsResponse = {
  success?: boolean
  records?: DnsRecord[]
  source?: string
  configured?: boolean
  note?: string
}

export default function Dns() {
  const { data, isLoading, isError } = useQuery<DnsResponse>({
    queryKey: ['control-center', 'dns'],
    queryFn: controlCenterApi.listDns,
  })

  const records = data?.records ?? []

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="DNS"
        description={
          data?.configured
            ? `${records.length} records declared. Source: ${data.source}`
            : 'DNS registry not configured.'
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load DNS records.
        </div>
      ) : null}

      {data?.note ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400">
          {data.note}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Hostname</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Value</th>
              <th className="px-4 py-2.5 font-medium">TTL</th>
              <th className="px-4 py-2.5 font-medium">Provider</th>
              <th className="px-4 py-2.5 font-medium">Purpose</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  No DNS records declared in <code className="font-mono">dns.yaml</code>.
                </td>
              </tr>
            ) : (
              records.map((r, i) => (
                <tr key={`${r.hostname}-${r.type}-${i}`} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{r.hostname}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-mono uppercase">
                      {r.type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.value}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.ttl ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.provider ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.purpose ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
